/**
 * OTA Updater Service - AWS Server Integration
 * Using @capgo/capacitor-updater with custom AWS backend
 */

import { App } from '@capacitor/app'
import { Capacitor, CapacitorHttp } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'

import type {
  OTAConfig,
  OTAManifest,
  DeviceInfo,
  UpdateCheckResult,
  UpdateProgress,
  OTAChannel,
  ServerBundleResponse,
} from './types'
import { OTA_BASE, OTA_CHANNEL, OTA_COOLDOWN_MIN, STORAGE_KEYS, validateOTAConfig, buildManifestUrl, buildAbsoluteUrl, isAllowedBundleUrl } from './config'
import { compareSemver, isNewer, getStorageItem, setStorageItem, safeJsonParse } from './utils'
import { shouldApplyBundle, rememberAppliedBundle, forgetAppliedBundle, getAppliedBundleBuild, rememberAppliedBuild, withOtaRequestHeaders, rememberEtag } from './versionGate'

/**
 * Whether it is safe to swap the bundle in immediately rather than waiting for the
 * next cold start.
 *
 * A reload tears down the whole JS context: the Redux store, any open modal, an
 * in-flight order accept/decline, and — worst — a print in progress, which runs
 * through a setTimeout and nested Bluetooth callbacks that a reload kills silently.
 * So the default is "never", and the app layer opts in by describing when it has
 * nothing to lose (see setSafeToReloadCheck in App.tsx).
 *
 * The predicate lives here rather than the updater importing the store directly,
 * so src/ota stays free of Redux coupling.
 */
let isSafeToReload: () => boolean = () => false

export function setSafeToReloadCheck(check: () => boolean): void {
  isSafeToReload = check
}

class OTAUpdaterService {
  private static instance: OTAUpdaterService
  private config: OTAConfig
  private lastCheckTime = 0
  private isUpdating = false
  private progressListeners = new Set<(progress: UpdateProgress) => void>()

  private constructor() {
    // Initialize configuration
    this.config = {
      baseUrl: OTA_BASE,
      channel: OTA_CHANNEL,
      cooldownMinutes: OTA_COOLDOWN_MIN,
      debug: process.env.NODE_ENV === 'development',
      allowChannelSwitch: process.env.NODE_ENV === 'development',
    }

    // Load persisted channel
    const savedChannel = getStorageItem(STORAGE_KEYS.CURRENT_CHANNEL)
    if (savedChannel && this.config.allowChannelSwitch) {
      this.config.channel = savedChannel as OTAChannel
    }

    // Validate configuration
    validateOTAConfig()

    // Set up Capacitor Updater event listeners
    this.setupEventListeners()
  }

  static getInstance(): OTAUpdaterService {
    if (!OTAUpdaterService.instance) {
      OTAUpdaterService.instance = new OTAUpdaterService()
    }
    return OTAUpdaterService.instance
  }

  /**
   * Set up Capacitor Updater event listeners
   */
  private setupEventListeners(): void {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    try {
      // Listen to download progress events
      CapacitorUpdater.addListener('download', (info) => {
        this.log('Download event', info)
        const progress: UpdateProgress = {
          progress: info.percent || 0,
          stage: 'downloading',
          message: `Downloading... ${info.percent || 0}%`,
        }
        this.notifyProgressListeners(progress)
      })

      // Listen to download complete
      CapacitorUpdater.addListener('downloadComplete', (info) => {
        this.log('Download complete', info)
        const progress: UpdateProgress = {
          progress: 90,
          stage: 'verifying',
          message: 'Download complete, verifying...',
        }
        this.notifyProgressListeners(progress)
      })

      // Listen to update available
      CapacitorUpdater.addListener('updateAvailable', (info) => {
        this.log('Update available', info)
        const progress: UpdateProgress = {
          progress: 0,
          stage: 'downloading',
          message: 'Update found, starting download...',
        }
        this.notifyProgressListeners(progress)
      })

    } catch (error) {
      this.logError('Failed to setup event listeners', error)
    }
  }

  /**
   * Add progress listener
   */
  addProgressListener(listener: (progress: UpdateProgress) => void): () => void {
    this.progressListeners.add(listener)
    return () => this.progressListeners.delete(listener)
  }

  /**
   * Notify all progress listeners
   */
  private notifyProgressListeners(progress: UpdateProgress): void {
    this.progressListeners.forEach(listener => {
      try {
        listener(progress)
      } catch (error) {
        this.logError('Progress listener error', error)
      }
    })
  }

  /**
   * Initialize the updater (call early in app lifecycle)
   */
  async initUpdater(channel?: string): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.log('Web platform detected - OTA updates disabled')
        return
      }

      if (channel && this.config.allowChannelSwitch) {
        this.config.channel = channel as OTAChannel
        setStorageItem(STORAGE_KEYS.CURRENT_CHANNEL, channel)
      }

      this.log('OTA updater initialized', {
        baseUrl: this.config.baseUrl,
        channel: this.config.channel,
        cooldown: this.config.cooldownMinutes,
      })

    } catch (error) {
      this.logError('Failed to initialize OTA updater', error)
    }
  }

  /**
   * Get current device and app information
   */
  async getOtaInfo(): Promise<DeviceInfo> {
    const [appInfo, currentBundle] = await Promise.all([
      App.getInfo(),
      CapacitorUpdater.current().catch(() => null),
    ])

    const bundleVersion = (currentBundle as any)?.version || getStorageItem(STORAGE_KEYS.BUNDLE_VERSION)

    // Construct full native version with build number for accurate comparison
    const fullNativeVersion = `${appInfo.version}+${appInfo.build}`

    const deviceInfo = {
      nativeVersion: fullNativeVersion, // Now includes build number
      buildNumber: appInfo.build,
      bundleVersion,
      channel: this.config.channel,
      platform: Capacitor.getPlatform(),
    }

    this.log('[DEBUG] Device info retrieved', {
      nativeVersion: deviceInfo.nativeVersion,
      buildNumber: deviceInfo.buildNumber,
      bundleVersion: deviceInfo.bundleVersion,
      currentBundle: currentBundle,
      fromStorage: getStorageItem(STORAGE_KEYS.BUNDLE_VERSION),
      appVersion: appInfo.version,
      appBuild: appInfo.build,
    })

    return deviceInfo
  }

  /**
   * Build manifest URL for current channel - ALWAYS absolute
   */
  private getManifestUrl(): string {
    return buildManifestUrl(this.config.channel)
  }

  /**
   * Fetch manifest from AWS server with ETag caching
   */
  private async fetchManifest(): Promise<{ manifest?: OTAManifest; notModified?: boolean }> {
    const url = this.getManifestUrl()
    // Use version gate headers with app version info for server-side filtering
    const headers = await withOtaRequestHeaders()

    this.log('Fetching manifest', { url, headers })

    // SANITY CHECK: Test health endpoint first
    try {
      const healthUrl = buildAbsoluteUrl('/health')
      this.log('Testing health endpoint:', healthUrl)
      const healthResponse = await CapacitorHttp.get({
        url: healthUrl,
        headers: { 'Accept': 'application/json' },
      })
      this.log('Health check result:', { status: healthResponse.status, data: healthResponse.data })
    } catch (healthError) {
      console.error('[OTA] Health check failed:', healthError)
    }

    try {
      // Use CapacitorHttp instead of fetch
      const response = await CapacitorHttp.get({
        url,
        headers,
      })

      // Gated behind config.debug (unlike a plain console.log): a shared/debug
      // tablet's logcat is otherwise readable by anyone with adb access, and this
      // dumps bundle IDs, URLs, and ETags on every check.
      this.log('Manifest response status:', response.status)
      this.log('Manifest response body:', JSON.stringify(response.data))
      this.log('Manifest response headers:', JSON.stringify(response.headers))

      // Two distinct "nothing for you" signals, neither of which is an error:
      //   304 — our If-None-Match matched, the manifest is unchanged.
      //   204 — the server's version gate decided this native build is already at
      //         or ahead of the channel's latest bundle, so it returns no content.
      // Verified against the server: build 83 -> 200 + bundle, build 84 (== latest)
      // and build 99 -> 204 empty. Treating 204 as success used to fall through to
      // the bundle validation below and throw on every launch of an up-to-date app.
      if (response.status === 304 || response.status === 204) {
        console.log(`[OTA] No update available (HTTP ${response.status})`)
        this.log(`Manifest not modified (${response.status})`)
        return { notModified: true }
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP ${response.status}: ${response.url}`)
      }

      const serverResponse = response.data as ServerBundleResponse

      // Store ETag for next request using version gate system
      const etag = response.headers?.etag || response.headers?.ETag
      await rememberEtag(etag)

      // Basic validation
      if (!serverResponse.bundle?.bundleId || !serverResponse.bundle?.url || !serverResponse.bundle?.checksum) {
        console.log('[OTA] Server response missing bundle data:', JSON.stringify(serverResponse))
        throw new Error('Invalid server response format - bundle missing or incomplete')
      }

      // Convert server format to expected manifest format - ensure bundle URL is absolute
      const bundleUrl = serverResponse.bundle.url.startsWith('http')
        ? serverResponse.bundle.url
        : buildAbsoluteUrl(serverResponse.bundle.url)

      // The manifest is untrusted input (server response, possibly MITM'd) — refuse to
      // download/apply a bundle from any origin other than our own OTA server.
      if (!isAllowedBundleUrl(bundleUrl)) {
        throw new Error(`Refusing bundle from disallowed origin: ${bundleUrl}`)
      }

      const manifest: OTAManifest = {
        appId: 'com.deliverider.seller',
        channel: this.config.channel,
        minNative: '1.0',
        latest: {
          version: serverResponse.bundle.bundleId,
          url: bundleUrl,
          sha256: serverResponse.bundle.checksum,
          createdAt: new Date().toISOString(),
        },
        etag: etag || '',
      }

      this.log('Manifest fetched successfully', {
        bundleId: serverResponse.bundle.bundleId,
        channel: manifest.channel,
        url: manifest.latest.url,
        etag,
      })

      return { manifest }

    } catch (error) {
      throw error
    }
  }

  /**
   * Check if update is available and applicable
   */
  async checkForUpdate(): Promise<UpdateCheckResult> {
    try {
      this.log('[DEBUG] checkForUpdate() called - starting update check')

      if (!Capacitor.isNativePlatform()) {
        this.log('[DEBUG] Not native platform, skipping')
        return { available: false, reason: 'error', error: 'Android platform required' }
      }

      // Check cooldown period
      const now = Date.now()
      const cooldownMs = this.config.cooldownMinutes * 60 * 1000
      const lastCheckTime = parseInt(getStorageItem(STORAGE_KEYS.LAST_CHECK_TIME, '0'))

      if (now - lastCheckTime < cooldownMs) {
        this.log('Skipping check due to cooldown', {
          lastCheck: new Date(lastCheckTime),
          nextCheck: new Date(lastCheckTime + cooldownMs),
        })
        return { available: false, reason: 'cooldown' }
      }

      // Update last check time
      setStorageItem(STORAGE_KEYS.LAST_CHECK_TIME, now.toString())

      const result = await this.fetchManifest()

      if (result.notModified) {
        return { available: false, reason: 'not-modified' }
      }

      if (!result.manifest) {
        return { available: false, reason: 'error', error: 'No manifest received' }
      }

      const manifest = result.manifest
      const deviceInfo = await this.getOtaInfo()

      // Check minimum native version requirement
      if (manifest.minNative && compareSemver(deviceInfo.nativeVersion, manifest.minNative) < 0) {
        this.log('Update requires newer native version', {
          current: deviceInfo.nativeVersion,
          required: manifest.minNative,
        })
        return { available: false, reason: 'min-native', manifest }
      }

      // Skip bundles that look like timestamps (old artifacts)
      const bundleVersion = manifest.latest.version
      if (/^\d{13}$/.test(bundleVersion)) {
        this.log('Ignoring timestamp-based bundle version', {
          version: bundleVersion,
          reason: 'Legacy timestamp format',
        })
        return { available: false, reason: 'invalid-version', manifest }
      }

      // ⛔ VERSION GATE: Never downgrade below native or current OTA
      const shouldApply = await shouldApplyBundle(bundleVersion)
      if (!shouldApply) {
        this.log('[VERSION GATE] Bundle rejected - would be a downgrade', {
          bundleId: bundleVersion,
          nativeVersion: deviceInfo.nativeVersion,
          bundleVersion: deviceInfo.bundleVersion,
        })
        return {
          available: false,
          reason: 'downgrade-prevented',
          manifest,
        }
      }

      this.log('Update available', {
        current: deviceInfo.bundleVersion || 'none',
        latest: manifest.latest.version,
      })

      return { available: true, manifest }

    } catch (error) {
      this.logError('Update check failed', error)
      return { available: false, reason: 'error', error: String(error) }
    }
  }

  /**
   * Download and install an update using Capgo APIs
   */
  async downloadAndInstall(
    manifest: OTAManifest,
    onProgress?: (progress: UpdateProgress) => void
  ): Promise<boolean> {
    if (this.isUpdating) {
      throw new Error('Update already in progress')
    }

    try {
      this.isUpdating = true
      const { latest } = manifest

      const initialProgress = { progress: 0, stage: 'downloading', message: 'Starting download...' } as UpdateProgress
      onProgress?.(initialProgress)
      this.notifyProgressListeners(initialProgress)

      this.log('Starting update download', {
        version: latest.version,
        url: latest.url,
        sha256: latest.sha256,
      })

      console.log('[OTA] will download', latest.version, latest.url)

      // Download the bundle using Capgo
      const downloadResult = await CapacitorUpdater.download({
        url: latest.url,
        version: latest.version,
        sessionKey: latest.sha256, // Use SHA-256 as session key for verification
        checksum: latest.sha256,
      })

      const verifyProgress = { progress: 50, stage: 'verifying', message: 'Verifying download...' } as UpdateProgress
      onProgress?.(verifyProgress)
      this.notifyProgressListeners(verifyProgress)

      if (downloadResult.version !== latest.version) {
        throw new Error('Version mismatch after download')
      }

      this.log('Download result details', {
        id: downloadResult.id,
        version: downloadResult.version,
        willUseId: downloadResult.id || downloadResult.version,
      })

      const activatingProgress = { progress: 80, stage: 'activating', message: 'Preparing update...' } as UpdateProgress
      onProgress?.(activatingProgress)
      this.notifyProgressListeners(activatingProgress)

      const bundleId = downloadResult.id || downloadResult.version

      // set() vs next(): capgo's set() "sets the current bundle and IMMEDIATELY
      // reloads the app" — it is not a deferred activation, despite what this code
      // used to claim. next() is the deferred one: "the next bundle to be used when
      // the app is reloaded".
      //
      // So the choice of API *is* the choice of when the swap happens. Reloading a
      // seller mid-shift tears down the Redux store and kills an in-flight print
      // (which runs through a setTimeout and nested Bluetooth callbacks), so we only
      // take set() when the app layer says nothing would be lost.
      let applyNow = false
      try {
        applyNow = isSafeToReload()
      } catch {
        // A broken predicate must never force a disruptive reload. The update itself
        // is unaffected — the bundle is staged either way, this only defers the swap.
        applyNow = false
      }

      this.log(applyNow ? 'Activating bundle now' : 'Staging bundle for next launch', bundleId)

      // The two paths persist at different times, and it matters:
      //
      // next() does not reload, so the deferred path can activate first and only
      // record success afterwards. Writing the version gate before a next() that
      // throws would mark the bundle applied when it never was, and shouldApplyBundle
      // would then skip it forever.
      //
      // set() DOES reload, so on the immediate path nothing after it runs and the
      // writes have to happen first — with a rollback if set() itself throws.
      // Snapshot the AUTHORITATIVE gate, not the STORAGE_KEYS.BUNDLE_VERSION mirror.
      // That mirror is blanked by rollbackLast() and clearOTACache(), so restoring from
      // it can write back an empty value and reopen a downgrade window.
      const previousAppliedBuild = await getAppliedBundleBuild()

      if (!applyNow) {
        await CapacitorUpdater.next({ id: bundleId })
        setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_ID, bundleId)
        // bundleId is capgo's internal random id, which carries no version — keep the
        // real version alongside it so the promote path can compare builds.
        setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_VERSION, latest.version)
      }

      setStorageItem(STORAGE_KEYS.BUNDLE_VERSION, latest.version)
      await rememberAppliedBundle(latest.version)
      setStorageItem(STORAGE_KEYS.FAILED_ATTEMPTS, '0')

      if (applyNow) {
        // Any bundle staged by an earlier session is now obsolete. Left in place, the
        // next boot would promote that OLDER bundle over this newer one — a downgrade
        // that slips past the version gate because the gate only guards downloads.
        setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_ID, '')
        setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_VERSION, '')

        // Clearing our own marker is not enough: capgo keeps its own NEXT_VERSION pref
        // from the earlier next(), and installNext() would activate that older bundle
        // the next time the app backgrounds. There is no public API to clear it, so
        // point it at the bundle we are about to activate — installNext then sees
        // next === current and does nothing.
        await CapacitorUpdater.next({ id: bundleId }).catch(() => {
          // Non-fatal: worst case capgo re-activates this same bundle.
        })
      }

      const completeProgress = {
        progress: 100,
        stage: 'complete',
        message: applyNow ? 'Update ready, restarting...' : 'Update ready for next app start!',
      } as UpdateProgress
      onProgress?.(completeProgress)
      this.notifyProgressListeners(completeProgress)

      this.log('Update downloaded and activated', {
        version: latest.version,
        bundleId: downloadResult.version,
        applyNow,
      })

      console.log(`[OTA] ${applyNow ? 'applying now (nothing in progress to lose)' : 'staged for next launch'}`, latest.version)

      if (applyNow) {
        // Re-check immediately before committing. The decision above was taken before
        // the 'complete' progress event, which flips isUpdating false and re-enables
        // the login form — so a seller can start a login in the gap between the two.
        // The gate writes above are correct either way; only the swap timing changes.
        let stillSafe = false
        try {
          stillSafe = isSafeToReload()
        } catch {
          stillSafe = false
        }

        if (!stillSafe) {
          console.log('[OTA] conditions changed while finishing; deferring to next launch', latest.version)
          await CapacitorUpdater.next({ id: bundleId })
          setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_ID, bundleId)
          setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_VERSION, latest.version)
          return true
        }

        // Deliberately last: this reloads the webview, so nothing below it runs and
        // every write above is already committed.
        try {
          await CapacitorUpdater.set({ id: bundleId })
        } catch (setError) {
          // Roll the gate back. Without this the bundle counts as applied while the app
          // is still running the old one, and every future check skips it — the update
          // silently never lands. On a device that has never taken an OTA there is no
          // previous value, so restoring is not enough and the key must be removed.
          if (previousAppliedBuild > 0) {
            await rememberAppliedBuild(previousAppliedBuild)
          } else {
            await forgetAppliedBundle()
          }
          setStorageItem(STORAGE_KEYS.BUNDLE_VERSION, '')
          throw setError
        }
      }

      return true

    } catch (error) {
      // Increment failed attempts
      const failedAttempts = parseInt(getStorageItem(STORAGE_KEYS.FAILED_ATTEMPTS, '0')) + 1
      setStorageItem(STORAGE_KEYS.FAILED_ATTEMPTS, failedAttempts.toString())

      // Notify progress listeners about the error
      const errorProgress = {
        progress: 0,
        stage: 'error',
        message: 'Update failed',
        error: String(error),
      } as UpdateProgress
      onProgress?.(errorProgress)
      this.notifyProgressListeners(errorProgress)

      this.logError('Failed to download/install update', error)
      throw error
    } finally {
      this.isUpdating = false
    }
  }

  /**
   * Check for updates and auto-install if available (non-blocking)
   */
  async checkForUpdateOnLaunch(): Promise<boolean> {
    try {
      this.log('[DEBUG] checkForUpdateOnLaunch() called')

      const result = await this.checkForUpdate()

      this.log('[DEBUG] checkForUpdate result', {
        available: result.available,
        reason: result.reason,
        hasManifest: !!result.manifest,
      })

      if (!result.available || !result.manifest) {
        this.log('[DEBUG] Update not available, exiting early')
        return false
      }

      this.log('Auto-installing update on launch')

      await this.downloadAndInstall(result.manifest)
      return true

    } catch (error) {
      this.logError('Auto-update on launch failed', error)
      return false
    }
  }

  /**
   * Force check for updates (ignores cooldown)
   */
  async forceCheck(): Promise<UpdateCheckResult> {
    // Reset cooldown for forced check
    setStorageItem(STORAGE_KEYS.LAST_CHECK_TIME, '0')
    return this.checkForUpdate()
  }

  /**
   * Switch to a different channel (dev only)
   */
  async switchChannel(newChannel: OTAChannel): Promise<void> {
    if (!this.config.allowChannelSwitch) {
      throw new Error('Channel switching disabled in production')
    }

    this.config.channel = newChannel
    setStorageItem(STORAGE_KEYS.CURRENT_CHANNEL, newChannel)

    // Clear cached ETag when switching channels
    setStorageItem(STORAGE_KEYS.LAST_ETAG, '')

    this.log('Channel switched', { newChannel })
  }

  /**
   * Rollback to previous bundle if supported
   */
  async rollbackLast(): Promise<boolean> {
    try {
      this.log('Attempting rollback to previous bundle')

      // Try using Capgo's rollback if available
      if ('rollback' in CapacitorUpdater) {
        await (CapacitorUpdater as any).rollback()
      } else {
        // Fallback: reset to native bundle
        await CapacitorUpdater.reset()
      }

      // Clear stored bundle version
      setStorageItem(STORAGE_KEYS.BUNDLE_VERSION, '')

      this.log('Rollback successful')
      return true

    } catch (error) {
      this.logError('Rollback failed', error)
      return false
    }
  }

  /**
   * Clear OTA cache (ETag, last check time, etc.)
   */
  clearCache(): void {
    setStorageItem(STORAGE_KEYS.LAST_ETAG, '')
    setStorageItem(STORAGE_KEYS.LAST_CHECK_TIME, '0')
    setStorageItem(STORAGE_KEYS.BUNDLE_VERSION, '')
    this.log('OTA cache cleared')
  }

  /**
   * Clear all stored bundles from device
   */
  async clearAllBundles(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return
      }

      // List all bundles and delete them
      const bundles = await CapacitorUpdater.list()
      this.log('Found bundles to clear:', bundles)

      if (bundles.bundles) {
        for (const bundle of bundles.bundles) {
          if (bundle.id !== 'public') { // Don't delete the native bundle
            try {
              await CapacitorUpdater.delete({ id: bundle.id })
              this.log(`Deleted bundle: ${bundle.id}`)
            } catch (error) {
              // Don't log as error for expected cases (active bundle, non-existent bundle)
              const errorMessage = error instanceof Error ? error.message : String(error)
              if (errorMessage?.includes('does not exist') || errorMessage?.includes('cannot be deleted')) {
                this.log(`Skipped bundle ${bundle.id}: ${errorMessage}`)
              } else {
                this.logError(`Failed to delete bundle ${bundle.id}`, error)
              }
            }
          }
        }
      }

      // Deliberately does NOT call clearCache(). Deleting stale bundle files has
      // nothing to do with check-timing bookkeeping, and this runs on every launch:
      // zeroing LAST_CHECK_TIME there meant the cooldown was reset before every cold
      // start, so two launches seconds apart both hit the server no matter what
      // OTA_COOLDOWN_MIN said. It also blanked LAST_ETAG (forcing a full manifest
      // fetch instead of a 304) and BUNDLE_VERSION (which is why the version gate had
      // to move to Preferences to be trustworthy). Use clearOTACache() when you
      // actually want the cache reset.
      this.log('All OTA bundles cleared')

    } catch (error) {
      this.logError('Failed to clear bundles', error)
    }
  }

  /**
   * Notify Capgo that the app has started successfully
   * This prevents rollbacks due to failed updates
   */
  async notifyAppReady(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return
      }

      await CapacitorUpdater.notifyAppReady()
      this.log('App ready notification sent to Capgo')
    } catch (error) {
      this.logError('Failed to notify app ready', error)
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): OTAConfig & { isUpdating: boolean } {
    return {
      ...this.config,
      isUpdating: this.isUpdating,
    }
  }

  /**
   * Log debug information
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[OTA] ${message}`, data || '')
    }
  }

  /**
   * Log error information
   */
  private logError(message: string, error: any): void {
    console.error(`[OTA] ${message}`, error)
  }
}

// Export singleton instance
export const otaUpdater = OTAUpdaterService.getInstance()

// Export main functions as specified in requirements
export async function initUpdater(channel?: string): Promise<void> {
  return otaUpdater.initUpdater(channel)
}

export async function checkForUpdateOnLaunch(): Promise<boolean> {
  return otaUpdater.checkForUpdateOnLaunch()
}

/**
 * Promotes a bundle that a previous session staged via next().
 *
 * Must run at launch, before anything the user could lose.
 *
 * Capgo promotes a staged bundle from installNext(), which it calls when the app moves
 * to the BACKGROUND — not at process start, which just loads the current bundle. So the
 * normal background/return cycle promotes it fine, but a process that dies without that
 * graceful callback (crash, force-stop, OS kill under memory pressure) leaves the
 * bundle staged forever, and every later check re-downloads the same version. This
 * makes promotion deterministic regardless of how the previous session ended.
 *
 * At boot there is by definition nothing in flight, so this does not consult
 * isSafeToReload — deferring here is what would be wrong.
 */
export async function promoteStagedBundle(options?: { requireSafe?: boolean }): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  const pending = getStorageItem(STORAGE_KEYS.PENDING_BUNDLE_ID, '')
  if (!pending) {
    return
  }

  // At boot this is skipped on purpose. "Nothing in flight" means no seller work in
  // progress, not !isLogged — a restored session can flip isLogged true before this
  // runs, but the app has only just started, so there is no order, form or print to
  // lose and the session comes back via AuthStorage (async — a brief logged-out
  // window is expected, see setAuthOnInitEpic) after the reload.
  // Callers outside boot (logout) pass requireSafe so a login starting moments later
  // is not reloaded out from under.
  if (options?.requireSafe) {
    let safe = false
    try {
      safe = isSafeToReload()
    } catch {
      safe = false
    }
    if (!safe) {
      // Leave the markers intact — this is a "not yet", not a discard.
      console.log('[OTA] staged bundle left pending, not safe to swap right now')
      return
    }
  }

  const pendingVersion = getStorageItem(STORAGE_KEYS.PENDING_BUNDLE_VERSION, '')

  // Clear BEFORE activating. set() reloads the webview, so nothing after it runs — a
  // marker left in place would re-promote on every boot, which is a boot loop.
  setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_ID, '')
  setStorageItem(STORAGE_KEYS.PENDING_BUNDLE_VERSION, '')

  // Never promote a bundle that is not strictly newer than what is running. A marker
  // can outlive its usefulness — stage A, then a later logged-out session applies a
  // newer B — and promoting A here would be a silent downgrade.
  //
  // Compare the stored VERSION, not the id: capgo ids are random strings, so parsing a
  // build out of `pending` always yields 0 and the guard never fires.
  const buildOf = (v: string): number => {
    const m = v.match(/\+(\d+)$/)
    return m ? parseInt(m[1], 10) : 0
  }

  let current: Awaited<ReturnType<typeof CapacitorUpdater.current>> | null = null
  try {
    current = await CapacitorUpdater.current()
  } catch {
    current = null
  }

  const currentVersion = String(current?.bundle?.version || '')
  const currentBuild = buildOf(currentVersion)
  const pendingBuild = buildOf(pendingVersion)

  /**
   * Points capgo's own NEXT_VERSION at the running bundle so installNext() is a no-op
   * on the next background. Clearing our marker alone is not enough — capgo's pref
   * survives and would otherwise activate the discarded bundle behind our back.
   */
  const neutralizeCapgoNext = async () => {
    const currentId = current?.bundle?.id
    if (currentId) {
      await CapacitorUpdater.next({ id: currentId }).catch(() => {
        // Best effort — nothing better to do if capgo refuses.
      })
      return
    }

    // current() failed, so there is no id to retarget onto and capgo's NEXT_VERSION may
    // still point at the bundle we just discarded — installNext() would then activate
    // it on the next background. There is no clear-next API, so find any other bundle
    // to park the pointer on; a successfully-applied one is the safest choice.
    const queued = await CapacitorUpdater.getNextBundle().catch(() => null)
    if (!queued) {
      return
    }

    const listed = await CapacitorUpdater.list().catch(() => null)
    const parkOn = listed?.bundles?.find(b => b.id !== queued.id && b.status === 'success')
      || listed?.bundles?.find(b => b.id !== queued.id)

    if (parkOn) {
      await CapacitorUpdater.next({ id: parkOn.id }).catch(() => {
        console.warn('[OTA] capgo still has a discarded bundle queued and it could not be retargeted', queued)
      })
      return
    }

    console.warn('[OTA] capgo still has a discarded bundle queued and no other bundle exists to retarget onto', queued)
  }

  /**
   * Undoes the optimistic gate write from the deferred path.
   *
   * The staging path calls rememberAppliedBundle as soon as next() succeeds. If a later
   * boot then discards that bundle, the gate would still read "applied = staged" and
   * every future check would skip that build while the app runs the older one.
   */
  const restoreGateToRunningBundle = async () => {
    try {
      if (currentBuild) {
        await rememberAppliedBuild(currentBuild)
      } else {
        await forgetAppliedBundle()
      }
      setStorageItem(STORAGE_KEYS.BUNDLE_VERSION, currentVersion)
    } catch {
      // Best effort.
    }
  }

  const discard = async (reason: string, detail: Record<string, unknown>) => {
    console.log(`[OTA] discarding staged bundle, ${reason}`, detail)
    await restoreGateToRunningBundle()
    await neutralizeCapgoNext()
  }

  // Fail closed. A missing version means we cannot prove the staged bundle is newer,
  // and promoting on faith is exactly the downgrade this guard exists to stop.
  if (!pendingBuild) {
    await discard('no recorded version to verify', { pending })
    return
  }

  // What counts as "too old" depends on which signal we actually have.
  //
  // currentBuild is the only true "what is running". appliedBuild is NOT a substitute:
  // the deferred path writes the gate optimistically the moment next() succeeds, so
  // right after staging build N the gate already reads N while the app still runs the
  // older one. Treating that as a floor would discard every legitimate staged update
  // (max(91, 97) = 97, and 97 <= 97 discards) — the exact bug this replaces.
  const appliedBuild = await getAppliedBundleBuild()

  if (currentBuild) {
    if (pendingBuild <= currentBuild) {
      await discard('not newer than the running bundle', {
        staged: pendingVersion,
        current: currentVersion,
      })
      return
    }
  } else if (appliedBuild && pendingBuild < appliedBuild) {
    // current() failed, so appliedBuild is the only floor available. Compare strictly:
    // pendingBuild === appliedBuild is this bundle's own optimistic write and must
    // promote, while anything genuinely older is a stale marker and must not.
    await discard('older than the recorded applied build, and the running bundle is unknown', {
      staged: pendingVersion,
      appliedBuild,
    })
    return
  }

  try {
    console.log('[OTA] promoting bundle staged by a previous session', pendingVersion)
    await CapacitorUpdater.set({ id: pending })
  } catch (error) {
    // The marker is already cleared, so nothing will retry the promotion. Put the gate
    // back to the build actually running, otherwise this version counts as applied
    // while the app runs the old one and no future check re-downloads it. Restoring the
    // running build rather than zeroing it also keeps the downgrade guard armed.
    console.warn('[OTA] could not promote staged bundle; restoring the gate', error)
    await restoreGateToRunningBundle()
  }
}

let resumeCheckHandle: { remove: () => Promise<void> } | null = null

/**
 * Re-checks for updates whenever the app returns to the foreground.
 *
 * Without this the only check is at cold start, and Android apps are rarely killed —
 * a device that is only ever backgrounded can run stale code for days. OTA_COOLDOWN_MIN
 * was already documented as "minutes between resume checks", but nothing implemented it.
 *
 * No extra throttling is needed here: checkForUpdate() already enforces the cooldown
 * and returns reason:'cooldown' when called too soon, so a user flicking in and out of
 * the app costs at most one storage read.
 *
 * Safe to call more than once. Idempotent.
 */
export async function startResumeUpdateChecks(): Promise<void> {
  if (resumeCheckHandle || !Capacitor.isNativePlatform()) {
    return
  }

  try {
    resumeCheckHandle = await App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        return
      }
      void otaUpdater.checkForUpdateOnLaunch().catch(() => {
        // Non-fatal: a failed resume check just means we retry on the next resume.
      })
    })
  } catch (error) {
    console.warn('[OTA] could not register resume update checks', error)
  }
}

export async function stopResumeUpdateChecks(): Promise<void> {
  try {
    await resumeCheckHandle?.remove()
  } catch {
    // Nothing useful to do if the listener is already gone.
  }
  resumeCheckHandle = null
}

export async function forceCheck(): Promise<UpdateCheckResult> {
  return otaUpdater.forceCheck()
}

export async function getOtaInfo(): Promise<DeviceInfo> {
  return otaUpdater.getOtaInfo()
}

export async function switchChannel(newChannel: OTAChannel): Promise<void> {
  return otaUpdater.switchChannel(newChannel)
}

export async function rollbackLast(): Promise<boolean> {
  return otaUpdater.rollbackLast()
}

export function clearOTACache(): void {
  return otaUpdater.clearCache()
}

export async function clearAllBundles(): Promise<void> {
  return otaUpdater.clearAllBundles()
}

export async function notifyAppReady(): Promise<void> {
  return otaUpdater.notifyAppReady()
}

export function addProgressListener(listener: (progress: UpdateProgress) => void): () => void {
  return otaUpdater.addProgressListener(listener)
}
